import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, CheckCircle, Tag, EyeOff, Users, MessageSquare, Bell, IndianRupee, UserSearch, ClipboardList, Handshake, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { FollowUpsPanel } from "@/components/admin/FollowUpsPanel";
import { VisitorsPanel } from "@/components/admin/VisitorsPanel";

const STATS = [
  { key: "buyers", label: "Total Buyers", icon: UserSearch, color: "text-slate-900", to: "/admin/buyers" },
  { key: "active_requirements", label: "Active Requirements", icon: ClipboardList, color: "text-[hsl(var(--brand))]", to: "/admin/requirements" },
  { key: "new_enquiries", label: "New Enquiries", icon: Bell, color: "text-orange-600", to: "/admin/enquiries" },
  { key: "deals_in_process", label: "Deals In Process", icon: Handshake, color: "text-blue-600", to: "/admin/deals" },
  { key: "available", label: "Available Machines", icon: CheckCircle, color: "text-emerald-600", to: "/admin/machines" },
  { key: "sold", label: "Sold Machines", icon: Tag, color: "text-red-600", to: "/admin/deals" },
  { key: "pending_service_charges", label: "Pending Charges", icon: Receipt, color: "text-amber-600", to: "/admin/service-charges" },
  { key: "followups_today", label: "Follow-ups Today", icon: Bell, color: "text-orange-600", to: "/admin/crm-leads" },
  { key: "sellers", label: "Total Sellers", icon: Users, color: "text-slate-900", to: "/admin/sellers" },
];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <h1 className="font-display text-2xl font-extrabold text-slate-900">Dashboard</h1>
      <p className="mt-1 text-slate-500">Overview of your marketplace.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {STATS.map((s) => (
          <Link key={s.key} to={s.to} data-testid={`stat-${s.key}`} className="rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md">
            <s.icon className={`mb-2 ${s.color}`} size={22} />
            <p className={`font-display text-3xl font-extrabold ${s.color}`}>{data ? data[s.key] : "—"}</p>
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/admin/buyers" className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white">+ Add Buyer Requirement</Link>
        <Link to="/admin/machines/new" className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700">+ Add Machine</Link>
        <Link to="/admin/revenue" className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700">View Revenue</Link>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:max-w-md">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">Paid Revenue</p>
          <p className="font-display text-2xl font-extrabold text-emerald-700">₹{data ? new Intl.NumberFormat("en-IN").format(Math.round(data.paid_revenue || 0)) : "—"}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-600">Pending Revenue</p>
          <p className="font-display text-2xl font-extrabold text-amber-700">₹{data ? new Intl.NumberFormat("en-IN").format(Math.round(data.pending_revenue || 0)) : "—"}</p>
        </div>
      </div>

      {data?.recent_enquiries?.length > 0 && (
        <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-5 py-3">
            <h2 className="font-display font-bold text-slate-900">Recent Enquiries</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {data.recent_enquiries.map((e) => (
              <div key={e.id} className="flex items-center justify-between px-5 py-3 text-sm">
                <div>
                  <p className="font-semibold text-slate-900">{e.name} <span className="font-mono text-xs text-slate-400">{e.enquiry_code}</span></p>
                  <p className="text-slate-500">{e.machine_name}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{e.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <FollowUpsPanel />
      <VisitorsPanel />
    </div>
  );
}
